import {useMemo} from 'react';

import {useMetricStatisticsAggregationQueryQuery} from '../../../../../generated/graphql';
import {NetworkStatus} from '../../../../../state/graphql/apolloWithAbort';
import {EMPTY_FILTERS, toMongo} from '../../../../../util/filters';

export type MetricStatisticsAggregation = {
  min: {min: number; max: number};
  max: {min: number; max: number};
  last: {min: number; max: number};
};
export function useMetricStatisticsAggregation({
  currentKey,
  entityName,
  projectName,
  shouldSkipMetricStatistics,
}: {
  currentKey?: string;
  entityName: string;
  projectName: string;
  shouldSkipMetricStatistics: boolean;
}): {
  metricStatisticsAggregation: MetricStatisticsAggregation;
  metricStatisticsError?: Error;
  metricStatisticsLoading: boolean;
} {
  const {
    data: metricStatistics,
    error: metricStatisticsError,
    networkStatus,
  } = useMetricStatisticsAggregationQueryQuery({
    variables: {
      filters: JSON.stringify(toMongo(EMPTY_FILTERS)),
      limit: 10000,
      order: '-createdAt',
      metricName: currentKey ?? '', // this cannot be hit because the query will be skipped when `currentKey` doesn't exist
      entityName,
      projectName,
    },
    notifyOnNetworkStatusChange: true,
    skip: shouldSkipMetricStatistics,
  });
  const metricStatisticsLoading = networkStatus !== NetworkStatus.ready;

  const metricStatisticsAggregation = useMemo(() => {
    try {
      return metricStatistics?.project?.runs?.metricStatisticsAggregation ?? {};
    } catch (e) {
      console.error(e);
      return {};
    }
  }, [metricStatistics]);

  return useMemo(
    () => ({
      metricStatisticsAggregation,
      metricStatisticsError,
      metricStatisticsLoading,
    }),
    [
      metricStatisticsAggregation,
      metricStatisticsError,
      metricStatisticsLoading,
    ]
  );
}
